export const getIndexes = (arr: unknown[]) => {
  return [...Array.from({ length: arr.length }).keys()];
};

type ItemWithValue<K extends string> = {
  [key in K]: string;
} & {
  value: number;
};

export const getValue = <K extends string, T extends ItemWithValue<K>>(
  key: K,
  tag: string | undefined,
  arr: T[],
): T | undefined => {
  return arr.find((item) => item[key] === tag);
};

export const computeRadiusByValue = (val: number | undefined, maxR: number) => {
  if (val === undefined || val <= 0) return 0;
  return Math.floor((val / 100) * maxR);
};

export const getBaseRadiusForCircles = (
  outerRadius: number,
  circlesCount: number,
) => outerRadius / circlesCount;

export const createNestedCircles = (
  cx: number,
  cy: number,
  count: number,
  baseRadius: number,
  gap: number,
) => {
  return Array.from({ length: count }).map((_, i) => ({
    cx,
    cy,
    r: baseRadius + i * gap,
  }));
};

export const angleToSvgVector = (angle: number) => {
  const rad = (angle - 90) * (Math.PI / 180);

  const x = Math.cos(rad);
  const y = Math.sin(rad);

  const x1 = (x + 1) / 2;
  const y1 = (y + 1) / 2;
  const x2 = 1 - x1;
  const y2 = 1 - y1;

  return { x1, y1, x2, y2 };
};

export const circleToPath = ({
  cx,
  cy,
  r,
}: {
  cx: number;
  cy: number;
  r: number;
}) => {
  return `M ${cx + r} ${cy}
          A ${r} ${r} 0 1 0 ${cx - r} ${cy}
          A ${r} ${r} 0 1 0 ${cx + r} ${cy}`;
};

export const getSinCos = (angle: number) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * angle);
  const cos = Math.cos(-RADIAN * angle);
  return { sin, cos };
};

export const getLines = ({
  cx,
  cy,
  outerRadius,
  labelOffset,
  innerRadius,
  sin,
  cos,
  widthScale,
  startAngle,
  containerHeight,
  containerWidth,
}: {
  cx: number;
  cy: number;
  outerRadius: number;
  innerRadius: number;
  labelOffset: number;
  sin: number;
  cos: number;
  widthScale: number;
  startAngle: number | undefined;
  containerHeight: number;
  containerWidth: number;
}) => {
  if (startAngle === undefined)
    return { sx: cx, sy: cy, mx: cx, my: cy, ex: cx, ey: cy };

  const sx = cx + innerRadius * cos;
  const sy = cy + innerRadius * sin;

  const scaledDistance =
    startAngle >= 350 || startAngle <= 10
      ? containerWidth / 2
      : startAngle >= 80 && startAngle <= 100
        ? containerHeight / 2
        : startAngle >= 170 && startAngle <= 190
          ? containerWidth / 2
          : startAngle >= 260 && startAngle <= 280
            ? containerHeight / 2
            : (outerRadius + labelOffset) * widthScale;

  const mx = cx + scaledDistance * cos;
  const my = cy + scaledDistance * sin;

  return { sx, sy, mx, my };
};

export const getEvenLines = ({
  cx,
  cy,
  outerRadius,
  labelOffset,
  innerRadius,
  sin,
  cos,
  widthScale,
  startAngle,
}: {
  cx: number;
  cy: number;
  outerRadius: number;
  innerRadius: number;
  labelOffset: number;
  sin: number;
  cos: number;
  widthScale: number;
  startAngle: number | undefined;
}) => {
  if (startAngle === undefined)
    return { sx: cx, sy: cy, mx: cx, my: cy, ex: cx, ey: cy };

  const sx = cx + innerRadius * cos;
  const sy = cy + innerRadius * sin;

  const scaledDistance = (outerRadius + labelOffset) * widthScale;

  const mx = cx + scaledDistance * cos;
  const my = cy + scaledDistance * sin;

  return { sx, sy, mx, my };
};

export const getTextAnchor = ({
  cos,
}: {
  cos: number;
}): "start" | "end" | "middle" => {
  return cos >= 0 ? "start" : "end";
};

export const getVerticalOffset = ({
  sin,
  tagHeight,
}: {
  sin: number;
  tagHeight: number;
}) => {
  const baseOffset = 12;
  const topExtraOffset = 20;

  return sin < 0
    ? tagHeight / 2 + baseOffset + topExtraOffset
    : tagHeight / 2 + baseOffset;
};

export const getBaseTextPosition = ({
  cx,
  cy,
  midAngle = 0,
  outerRadius,
  textWidth,
  labelOffset = 120,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  textWidth: number;
  labelOffset?: number;
}) => {
  const { sin, cos } = getSinCos(midAngle);
  const centerR = outerRadius + labelOffset;

  const ex = cx + centerR * cos;
  const textAnchor = getTextAnchor({ cos });

  const textX = textAnchor === "start" ? ex + textWidth : ex;
  const textY = cy + centerR * sin;

  return { textX, textY };
};

export const getRectBoxPositionBaseOnTextPosition = ({
  textX,
  textY,
  textWidth,
  textHeight,
  paddingHorizontal,
  paddingVertical,
  textAnchor,
}: {
  textX: number;
  textY: number;
  textWidth: number;
  paddingHorizontal: number;
  paddingVertical: number;
  textHeight: number;
  textAnchor: "start" | "end" | "middle";
}) => {
  const rectWidth = textWidth + paddingHorizontal * 2;

  const rectX =
    textAnchor === "start"
      ? textX - paddingHorizontal
      : textX - textWidth - paddingHorizontal;

  const rectY = textY - textHeight + paddingVertical;

  const rectHeight = textHeight + paddingVertical * 2;

  return { rectX, rectY, rectWidth, rectHeight };
};

export const getLablePositionBasedOnTagTextPosition = ({
  textX,
  textY,
  width,
  textAnchor,
}: {
  width: number;
  textX: number;
  textY: number;
  textAnchor: "start" | "end" | "middle";
}) => {
  const labelX = textAnchor === "start" ? textX - width : textX;
  return {
    labelX,
    labelY: textY - 5, // TODO: fix this
  };
};

export const getIconPosition = ({
  textAnchor,
  labelX,
  labelY,
  labelWidth,
  offsetX = 33,
  offsetY = 40,
}: {
  textAnchor: "start" | "end" | "middle";
  labelX: number;
  labelY: number;
  labelWidth: number;
  offsetX?: number;
  offsetY?: number;
}) => {
  const x =
    textAnchor === "start" ? labelX - offsetX : labelX - labelWidth - offsetX;

  const y = labelY - offsetY;

  return { x, y };
};
